import { authService } from '@/lib/auth-service';
import type { UnresolvedVisualIdentity } from '@/types/document-types';
import type { HumanIdentityReview } from '@/types/identity-review-types';

export interface SourceEvidenceSelection {
  identity: UnresolvedVisualIdentity;
  documentId: string;
  storageDocumentId?: string;
  fileName?: string;
  humanReview?: HumanIdentityReview;
}

export const sourceReviewReason: Record<UnresolvedVisualIdentity['resolutionCode'], string> = {
  missing_source_region: 'We could not pinpoint this name on the page.',
  invalid_source_region: 'We could not pinpoint this name on the page.',
  crop_failed: 'We could not isolate this name on the page.',
  reader_failed: 'We could not read this name automatically.',
  insufficient_independent_readers: 'We could not get two independent readings of this name.',
  reader_disagreement: 'Our two independent readings of this name differed.',
  material_ambiguity: 'This name is too unclear to read automatically.',
  restricted_evidence_unavailable: 'The automatic reading of this name is no longer available.',
};

// Use a request local to the viewer. Private responses must never enter the
// shared HTTP client's error telemetry or a persistent query/store cache.
export async function requestSource(path: string, signal: AbortSignal): Promise<Response> {
  const token = await authService.getToken();
  signal.throwIfAborted();
  if (!token) throw new Error('Please sign in again to view the source.');
  const response = await fetch(`${import.meta.env.VITE_API_URL || ''}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include', cache: 'no-store', signal,
  });
  if (!response.ok) throw new Error('The source is unavailable. Retry or request the original document.');
  return response;
}

export interface NormalizedSourceBox { x: number; y: number; width: number; height: number }

export interface SourceReading { text: string; note: string }

function validBox(box: any): NormalizedSourceBox | null {
  if (!box || box.unit !== 'normalized'
    || ![box.x, box.y, box.width, box.height].every(Number.isFinite)
    || box.x < 0 || box.y < 0 || box.width <= 0 || box.height <= 0
    || box.x + box.width > 1 || box.y + box.height > 1) return null;
  return { x: box.x, y: box.y, width: box.width, height: box.height };
}

/**
 * What the independent readers saw, and the field they read. Readers crop the
 * located field, so their region is tighter than the extractor's stored one.
 */
export function readerEvidence(
  evidence: unknown, selection: SourceEvidenceSelection, documentVersionId: string,
): { readings: SourceReading[]; box: NormalizedSourceBox | null } {
  const value = evidence && typeof evidence === 'object' ? evidence as Record<string, any> : {};
  const usable = (Array.isArray(value.readings) ? value.readings : []).filter((reading: any) =>
    Array.isArray(reading?.occurrenceIds) && reading.occurrenceIds.includes(selection.identity.occurrenceId));
  const seen = new Set<string>();
  const readings = usable.flatMap((reading: any) => {
    const text = typeof reading.verbatimText === 'string' ? reading.verbatimText.trim() : '';
    if (!text || seen.has(text)) return [];
    seen.add(text);
    return [{ text, note: typeof reading.ambiguityReason === 'string' ? reading.ambiguityReason.trim() : '' }];
  });
  const region = usable.map((reading: any) => reading.sourceRegion).find((region: any) =>
    region?.documentId === selection.documentId && region.documentVersionId === documentVersionId
    && region.pageNumber === selection.identity.pageNumber && validBox(region.boundingBox));
  return { readings, box: region ? validBox(region.boundingBox) : null };
}

export async function sourceFingerprint(bytes: ArrayBuffer): Promise<{ documentId: string; documentVersionId: string }> {
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), value => value.toString(16).padStart(2, '0')).join('');
  return { documentId: `document-${hash.slice(-32)}`, documentVersionId: `document-version-${hash}` };
}

export async function matchingSourceBox(
  evidence: unknown, selection: SourceEvidenceSelection, documentVersionId: string,
): Promise<NormalizedSourceBox | null> {
  if (!evidence || typeof evidence !== 'object') return null;
  const value = evidence as Record<string, any>;
  const region = value.sourceRegion;
  const identity = selection.identity;
  if (value.reviewEvidenceId !== identity.reviewEvidenceId
    || !Array.isArray(value.occurrenceIds) || !value.occurrenceIds.includes(identity.occurrenceId)
    || !region || region.documentId !== selection.documentId || region.documentVersionId !== documentVersionId
    || region.pageNumber !== identity.pageNumber
    || (identity.cropHash && value.cropHash !== identity.cropHash)) return null;
  const box = validBox(region.boundingBox);
  if (!box) return null;
  // The public ID fingerprints the complete region, rather than exposing its
  // internal regionId. Match the same canonical JSON used by the server.
  const canonical = (value: any): string => value && typeof value === 'object'
    ? Array.isArray(value) ? `[${value.map(canonical).join(',')}]`
      : `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`
    : JSON.stringify(value);
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical(region)))), value => value.toString(16).padStart(2, '0')).join('');
  if (`source-region-${digest.slice(0, 32)}` !== identity.sourceRegionId) return null;
  return box;
}
